import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import {
  ArrowLeft,
  Save,
  Clock,
  FileText,
  Plus,
  Edit,
  X,
  Trash2
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const OnlineTestEdit = () => {
  const navigate = useNavigate()
  const { testId } = useParams()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [formData, setFormData] = useState({
    testName: '',
    startDate: '',
    endDate: '',
    testStartTime: '',
    testStartPeriod: 'AM',
    testCloseTime: '',
    testClosePeriod: 'PM',
    duration: 60,
    totalMarks: 0,
    totalQuestions: 0
  })

  const [testQuestions, setTestQuestions] = useState([])
  const [questionEdits, setQuestionEdits] = useState({})

  const { getOnlineTests, getOnlineTestQuestions, updateDocument, deleteOnlineTestQuestion, updateOnlineTestQuestion } = useFirebase()

  useEffect(() => {
    loadTestData()
  }, [testId])

  const loadTestData = async () => {
    try {
      setLoading(true)

      // Load test metadata
      const tests = await getOnlineTests()
      const test = tests.find(t => t.id === testId)

      if (!test) {
        toast.error('Test not found')
        navigate('/home?tab=online')
        return
      }

      // Helper function for date formatting
      const formatDateForInput = (dateValue) => {
        if (!dateValue) return ''
        try {
          let date
          if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            date = dateValue.toDate()
          } else {
            date = new Date(dateValue)
          }
          return date.toISOString().split('T')[0]
        } catch (error) {
          return ''
        }
      }

      setFormData({
        testName: test.testName || '',
        startDate: formatDateForInput(test.startDate),
        endDate: formatDateForInput(test.endDate),
        testStartTime: test.testStartTime ? test.testStartTime.split(' ')[0] : '',
        testStartPeriod: (test.testStartTime && test.testStartTime.includes('PM')) ? 'PM' : 'AM',
        testCloseTime: test.testCloseTime ? test.testCloseTime.split(' ')[0] : '',
        testClosePeriod: (test.testCloseTime && test.testCloseTime.includes('PM')) ? 'PM' : 'AM',
        duration: test.duration || 60,
        totalMarks: test.totalMarks || 0,
        totalQuestions: test.totalQuestions || 0
      })

      // Load existing test questions
      const questions = await getOnlineTestQuestions(testId)
      const withData = questions || []
      setTestQuestions(withData)
      // prepare editable copies for each question so fields are always editable
      const editMap = {}
      withData.forEach((q) => {
        const optionTexts = q.optionTexts || {}
        const optionArray = q.options || []
        editMap[q.id] = {
          questionName: q.questionName || q.question || '',
          options: [
            optionTexts.optionA || optionArray[0] || '',
            optionTexts.optionB || optionArray[1] || '',
            optionTexts.optionC || optionArray[2] || '',
            optionTexts.optionD || optionArray[3] || ''
          ],
          correctAnswer: q.optionB ? 1 : q.optionC ? 2 : q.optionD ? 3 : 0,
          explanation: q.explanation || '',
          marks: q.marks || 0
        }
      })
      setQuestionEdits(editMap)

    } catch (error) {
      console.error('Error loading test data:', error)
      toast.error('Failed to load test data')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSaveTest = async () => {
    try {
      if (!formData.testName.trim()) {
        toast.error('Please enter a test name')
        return
      }

      setSaving(true)

      // Convert 12-hour values to 24-hour for the Date object
      const get24HourTime = (timeStr, period) => {
        if (!timeStr) return '00:00';
        const [hours, minutes] = timeStr.split(':');
        let h = parseInt(hours);
        if (period === 'PM' && h !== 12) h += 12;
        if (period === 'AM' && h === 12) h = 0;
        return `${h.toString().padStart(2, '0')}:${minutes}`;
      };

      const start24 = get24HourTime(formData.testStartTime, formData.testStartPeriod);
      const close24 = get24HourTime(formData.testCloseTime, formData.testClosePeriod);

      // Build date objects from combined date and time
      const startDateObj = new Date(formData.startDate + 'T' + start24)
      const endDateObj = new Date(formData.endDate + 'T' + close24)

      if (startDateObj >= endDateObj) {
        toast.error('End date/time must be after start date/time')
        setSaving(false)
        return
      }

      const updateData = {
        testName: formData.testName,
        startDate: startDateObj,
        endDate: endDateObj,
        testStartTime: `${formData.testStartTime} ${formData.testStartPeriod}`,
        testCloseTime: `${formData.testCloseTime} ${formData.testClosePeriod}`,
        duration: parseInt(formData.duration) || 60,
        totalMarks: parseInt(formData.totalMarks) || 0,
        totalQuestions: testQuestions.length
      }

      await updateDocument('onlineTests', testId, updateData)
      toast.success('Test updated successfully!')
      navigate('/home?tab=online')
    } catch (error) {
      console.error('Error saving test:', error)
      toast.error('Failed to save test')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveEdit = async (questionId) => {
    const editingQuestionData = questionEdits[questionId]
    if (!editingQuestionData) return
    try {
      setSaving(true)

      const updateData = {
        questionName: editingQuestionData.questionName,
        explanation: editingQuestionData.explanation,
        marks: parseInt(editingQuestionData.marks) || 0,
        optionA: editingQuestionData.correctAnswer === 0,
        optionB: editingQuestionData.correctAnswer === 1,
        optionC: editingQuestionData.correctAnswer === 2,
        optionD: editingQuestionData.correctAnswer === 3,
        optionTexts: {
          optionA: editingQuestionData.options[0],
          optionB: editingQuestionData.options[1],
          optionC: editingQuestionData.options[2],
          optionD: editingQuestionData.options[3]
        },
        updatedAt: new Date()
      }

      await updateOnlineTestQuestion(testId, questionId, updateData)

      // Reload questions
      const questions = await getOnlineTestQuestions(testId)
      const withData = questions || []
      setTestQuestions(withData)
      const editMap = { ...questionEdits }
      withData.forEach((q) => {
        const optionTexts = q.optionTexts || {}
        const optionArray = q.options || []
        editMap[q.id] = editMap[q.id] || {
          questionName: q.questionName || q.question || '',
          options: [
            optionTexts.optionA || optionArray[0] || '',
            optionTexts.optionB || optionArray[1] || '',
            optionTexts.optionC || optionArray[2] || '',
            optionTexts.optionD || optionArray[3] || ''
          ],
          correctAnswer: q.optionB ? 1 : q.optionC ? 2 : q.optionD ? 3 : 0,
          explanation: q.explanation || '',
          marks: q.marks || 0
        }
      })
      setQuestionEdits(editMap)

      toast.success('Question updated successfully!')
    } catch (error) {
      console.error('Error updating question:', error)
      toast.error('Failed to update question')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return
    }

    try {
      setSaving(true)
      await deleteOnlineTestQuestion(testId, questionId)

      // Reload questions
      const questions = await getOnlineTestQuestions(testId)
      const withData = questions || []
      setTestQuestions(withData)
      const editMap = { ...questionEdits }
      withData.forEach((q) => {
        if (!editMap[q.id]) {
          const optionTexts = q.optionTexts || {}
          const optionArray = q.options || []
          editMap[q.id] = {
            questionName: q.questionName || q.question || '',
            options: [
              optionTexts.optionA || optionArray[0] || '',
              optionTexts.optionB || optionArray[1] || '',
              optionTexts.optionC || optionArray[2] || '',
              optionTexts.optionD || optionArray[3] || ''
            ],
            correctAnswer: q.optionB ? 1 : q.optionC ? 2 : q.optionD ? 3 : 0,
            explanation: q.explanation || '',
            marks: q.marks || 0
          }
        }
      })
      setQuestionEdits(editMap)

      toast.success('Question deleted successfully!')
    } catch (error) {
      console.error('Error deleting question:', error)
      toast.error('Failed to delete question')
    } finally {
      setSaving(false)
    }
  }

  const handleEditFieldChange = (questionId, field, value) => {
    setQuestionEdits(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [field]: value
      }
    }))
  }

  const handleEditOptionChange = (questionId, index, value) => {
    setQuestionEdits(prev => {
      const current = prev[questionId] || { options: ['', '', '', ''], correctAnswer: 0, questionName: '', explanation: '', marks: 0 }
      const newOptions = [...(current.options || ['', '', '', ''])]
      newOptions[index] = value
      return {
        ...prev,
        [questionId]: {
          ...current,
          options: newOptions
        }
      }
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/home?tab=online')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-6 w-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Edit Online Test</h1>
              <span className="text-sm text-gray-600">Test ID: {testId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Test Metadata Section */}
      <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-blue-900">Test Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Test Name *</label>
            <input
              type="text"
              name="testName"
              value={formData.testName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter test name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Total Questions</label>
            <input
              type="number"
              value={testQuestions.length}
              readOnly
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="h-4 w-4 inline mr-1" />
              Test Start Time *
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="time"
                  name="testStartTime"
                  value={formData.testStartTime}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <select
                name="testStartPeriod"
                value={formData.testStartPeriod}
                onChange={handleInputChange}
                className="w-24 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold text-center"
                required
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="h-4 w-4 inline mr-1" />
              Test Close Time *
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="time"
                  name="testCloseTime"
                  value={formData.testCloseTime}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <select
                name="testClosePeriod"
                value={formData.testClosePeriod}
                onChange={handleInputChange}
                className="w-24 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold text-center"
                required
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
            <input
              type="number"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Total Marks</label>
            <input
              type="number"
              name="totalMarks"
              value={formData.totalMarks}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="0"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => navigate('/home?tab=online')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveTest}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <>
                <LoadingSpinner size="small" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Test
              </>
            )}
          </button>
        </div>
      </div>

      {/* Questions Section */}
      {testQuestions.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="h-4 w-4 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Test Questions ({testQuestions.length})</h2>
            </div>
            <button
              onClick={() => navigate(`/add-questions/${testId}?testName=${encodeURIComponent(formData.testName)}&from=online`)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add Question</span>
            </button>
          </div>

          <div className="space-y-4">
            {testQuestions.map((question, index) => {
              const editState = questionEdits[question.id] || {
                questionName: question.questionName || question.question || '',
                options: [
                  question.optionTexts?.optionA || question.options?.[0] || '',
                  question.optionTexts?.optionB || question.options?.[1] || '',
                  question.optionTexts?.optionC || question.options?.[2] || '',
                  question.optionTexts?.optionD || question.options?.[3] || ''
                ],
                correctAnswer: question.optionB ? 1 : question.optionC ? 2 : question.optionD ? 3 : 0,
                explanation: question.explanation || '',
                marks: question.marks || 0
              }

              return (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Question {index + 1}</h3>
                        <p className="text-sm text-gray-500">ID: {question.id}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveEdit(question.id)}
                        disabled={saving}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question.id)}
                        disabled={saving}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Question"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Question Text */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Question:</h4>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      value={editState.questionName}
                      onChange={(e) => handleEditFieldChange(question.id, 'questionName', e.target.value)}
                    />
                  </div>

                  {/* Marks */}


                  {/* Options */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Options:</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {['A', 'B', 'C', 'D'].map((letter, optIndex) => {
                        const isCorrect = editState.correctAnswer === optIndex
                        return (
                          <div key={letter} className="flex items-center space-x-2 p-2 rounded-lg border border-gray-200">
                            <input
                              type="radio"
                              name={`correct-${question.id}`}
                              checked={isCorrect}
                              onChange={() => handleEditFieldChange(question.id, 'correctAnswer', optIndex)}
                              className="w-4 h-4 text-green-600"
                            />
                            <span className="font-medium text-gray-700 w-6">{letter}.</span>
                            <input
                              type="text"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              value={editState.options[optIndex]}
                              onChange={(e) => handleEditOptionChange(question.id, optIndex, e.target.value)}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Explanation */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Explanation:</h4>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Explain why this is the correct answer..."
                      value={editState.explanation}
                      onChange={(e) => handleEditFieldChange(question.id, 'explanation', e.target.value)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {testQuestions.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Questions Yet</h3>
          <p className="text-gray-600 mb-4">This test doesn't have any questions yet. Add some to get started.</p>
          <button
            onClick={() => navigate(`/add-questions/${testId}?testName=${encodeURIComponent(formData.testName)}&from=online`)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Question</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default OnlineTestEdit
